<template>
  <screening-workflow-panel
    :review="review"
    :current-researcher="currentResearcher"
    :pending-connection.sync="
      referencesAwaitingYouFullTextReviewConnection
    "
    :pending-connection-query="
      $apollo.queries.referencesAwaitingYouFullTextReviewConnection
    "
    :history-connection="
      referencesByAwaitingYouFullTextReviewVoteHistoryConnection
    "
    :history-connection-query="
      $apollo.queries
        .referencesByAwaitingYouFullTextReviewVoteHistoryConnection
    "
    :search-input.sync="searchInput"
    :revoke-vote-func="revokeVote"
    :get-vote-by-current-user-func="getVoteByCurrentUser"
    :filtering-input="filteringInput"
    restrict-to-voting-only
    full-text-review
    @clear-filtering-input="$emit('clear-filtering-input')"
  >
    <template #empty-state>
      <page-content-placeholder
        icon="check"
        icon-color="success"
        prompt-text="Complete"
      >
        <template #message>
          All references awaiting you have been reviewed.
        </template>
      </page-content-placeholder>
    </template>
  </screening-workflow-panel>
</template>

<script>
import PageContentPlaceholder from '@/components/Common/PageContentPlaceholder';
import ScreeningWorkflowPanel from '@/components/ScreeningPageV2/ScreenTab/ScreeningWorkflowPanel';
import {
  GetReferencesAwaitingYouFullTextReview,
  GetReferencesByAwaitingYouFullTextReviewVoteHistory,
} from '@/graphql';
import {
  Review,
  Researcher,
  ReferenceOffsetConnection,
  ReferenceFilteringInputDto,
} from '@/models';
import { mapActions } from 'vuex';
import { isEqual } from 'lodash';

const PAGE_SIZE = 50;

export default {
  name: 'ReviewTabAwaitingYou',
  components: {
    ScreeningWorkflowPanel,
    PageContentPlaceholder,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    currentResearcher: {
      type: Researcher,
      required: true,
    },
    filteringInput: {
      type: ReferenceFilteringInputDto,
      required: true,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['clear-filtering-input'],
  data: () => ({
    searchInput: null,
    referencesAwaitingYouFullTextReviewConnection: null,
    referencesByAwaitingYouFullTextReviewVoteHistoryConnection: null,
  }),
  apollo: {
    referencesAwaitingYouFullTextReviewConnection: {
      query: GetReferencesAwaitingYouFullTextReview,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
          searchQuery: this.searchInput || null,
          hasTag: this.filteringInput.hasTag,
          hasKeyword: this.filteringInput.hasKeyword,
          hasAuthor: this.filteringInput.hasAuthor,
          hasPlacePublished: this.filteringInput.hasPlacePublished,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesAwaitingYouFullTextReview
        ),
      skip() {
        return !this.review?.id;
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
    referencesByAwaitingYouFullTextReviewVoteHistoryConnection: {
      query: GetReferencesByAwaitingYouFullTextReviewVoteHistory,
      variables() {
        return {
          reviewId: this.review.id,
          limit: PAGE_SIZE,
        };
      },
      update: (data) =>
        new ReferenceOffsetConnection(
          data.referencesByAwaitingYouFullTextReviewVoteHistory
        ),
      skip() {
        return (
          !this.review?.id ||
          !this.referencesAwaitingYouFullTextReviewConnection
        );
      },
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    },
  },
  watch: {
    active(active) {
      if (
        active &&
        this.referencesAwaitingYouFullTextReviewConnection
      ) {
        this.$apollo.queries.referencesAwaitingYouFullTextReviewConnection.refetch();
        this.$apollo.queries.referencesByAwaitingYouFullTextReviewVoteHistoryConnection.refetch();
      }
    },
    filteringInput(newInput, oldInput) {
      // Clear the cache when the filtering input changes to show the loading state.
      const changed = !isEqual(newInput, oldInput);
      if (this.active && changed) {
        this.referencesAwaitingYouFullTextReviewConnection = null;
      }
    },
  },
  methods: {
    ...mapActions({
      revokeVote: 'fullTextReviewVote/deleteWithCacheUpdate',
    }),
    getVoteByCurrentUser(reference) {
      return reference?.getFullTextReviewVoteBy(
        this.currentResearcher
      );
    },
  },
};
</script>
